import {
  ChangeEvent,
  SyntheticEvent,
  useContext,
  useEffect,
  useState,
} from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { MultiSelect, MultiSelectChangeEvent } from "primereact/multiselect";
import { useNavigate, useParams } from "react-router-dom";
import { useCreateInstitution } from "../../api/queries/institutions/useCreateInstitution";
import { useDeleteInstitution } from "../../api/queries/institutions/useDeleteInstitution";
import { useInstitution } from "../../api/queries/institutions/useInstitution";
import { useUpdateInstitution } from "../../api/queries/institutions/useUpdateInstitution";
import {
  CreateInstitutionPayload,
  UpdateInstitutionPayload,
} from "../../api/types/institution";
import { useQueryClient } from "react-query";
import { queryKeys } from "../../api/query";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { useChecklists } from "../../api/queries/checklists/useChecklists";
import { useCountries } from "../../api/queries/enum/useCountries";
import { useCountryStates } from "../../api/queries/enum/useCountryStates";
import { useJourneyTypes } from "../../api/queries/enum/useJourneyTypes";
import { Panel } from "primereact/panel";
import { userContext } from "../../context/user";
import { Message } from "primereact/message";
import { useContactPlatforms } from "../../api/queries/enum/useContactPlatforms";
import { CONTACT_PLATFORM_CODE } from "../../api/types/enum";
import { useCreateRepresentative } from "../../api/queries/representatives/useCreateRepresentative";
import {
  CreateRepresentativeContactPayload,
  CreateRepresentativePayload,
  Representative,
} from "../../api/types/representative";
import { SelectButton, SelectButtonChangeEvent } from "primereact/selectbutton";
import { useRepresentatives } from "../../api/queries/representatives/useRepresentatives";
import commonStyles from "../common.module.css";
import { useUploadInstitutionLogo } from "../../api/queries/institutions/useUploadInstitutionLogo";
import { loadFile } from "../../api/queries/files/loadFile";
import { useAppToast } from "../../context/toast";
import { fetchProfilePicture } from "../people/form";
import { deleteLogo } from "../../api/queries/files/deleteInstitutionLogo";
export const fetchInstitutionLogo = async (
  id: string,
  setUploadedInstitutionLogo: (
    image: { url: string | null; id: string | null } | null,
  ) => void,
): Promise<void> => {
  try {
    const response = await loadFile(id);
    setUploadedInstitutionLogo({ url: response.data.presignedUrl, id });
  } catch (error) {
    setUploadedInstitutionLogo(null);
  }
};

export default function InstitutionForm() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin } = useContext(userContext);
  const { ref: appToastRef } = useAppToast();
  const createInstituon = useCreateInstitution();
  const deleteInstituon = useDeleteInstitution();
  const updateInstituon = useUpdateInstitution(uuid);
  const representativeCreate = useCreateRepresentative();
  const { data: institutionResponse } = useInstitution(uuid);

  const { data: checklistOptions } = useChecklists(
    {
      limit: 100,
      offset: 0,
      source: "InstitutionDetail",
    },
    {
      enabled: isAdmin(),
    },
  );

  const { data: countryOptions } = useCountries();
  const { data: journeyTypeOptions } = useJourneyTypes();
  const { data: contactPlatformOptions } = useContactPlatforms();

  const [logoFile, setLogoFile] = useState<File | null>(null);
  const uploadInstitutionLogo = useUploadInstitutionLogo();
  const [uploadedInstitutionLogo, setUploadedInstitutionLogo] = useState<{
    url: string | null;
    id: string | null;
  } | null>({ url: null, id: null });

  const [name, setName] = useState<string>("");
  const onNameChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setName(event.target.value);

  const [web, setWeb] = useState<string>("https://");
  const handleWebChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setWeb(event.target.value);

  const [country, setCountry] = useState<string>("");
  const onCountryChange = (event: DropdownChangeEvent) =>
    setCountry(event.value);

  const { data: countryStateOptions } = useCountryStates({
    limit: 100,
    offset: 0,
    fCountryId: country,
  });

  const [state, setState] = useState<string>("");
  const onStateChange = (event: DropdownChangeEvent) => setState(event.value);

  const [city, setCity] = useState<string>("");
  const onCityChange = (event: ChangeEvent<HTMLInputElement>) =>
    setCity(event.target.value);

  const [context, setContext] = useState<string[]>([]);
  const onContextChange = (event: MultiSelectChangeEvent) =>
    setContext(event.value);

  const [checklists, setChecklists] = useState<string[]>([]);
  const onChecklistChange = (event: DropdownChangeEvent) =>
    setChecklists(event.value);

  // representative
  const tRepresentativeItem = (option: Representative) =>
    `${option.last_name || ""} ${option.first_name || ""}`;

  const tRepresentativeValue = (option: Representative, props: any) =>
    option ? (
      `${option.last_name || ""} ${option.first_name || ""}`
    ) : (
      <div>{props.placeholder || <>&nbsp;</>}</div>
    );

  const { data: representativeOptions } = useRepresentatives({
    limit: 100,
    offset: 0,
  });

  const [representative, setRepresentative] = useState<string>("");
  const onRepresentativeChange = (event: DropdownChangeEvent) =>
    setRepresentative(event.value);

  const representativeTypes: RepresentativeType[] = [
    { label: "Select existing", value: "existing" },
    { label: "Create new", value: "new" },
  ];
  const [representativeType, setRepresentativeType] = useState<string>(
    representativeTypeValue.EXISTING,
  );

  const onRepresentativeTypeChange = (event: SelectButtonChangeEvent) =>
    setRepresentativeType(event.value);

  const contactPlatformWorkEmail = contactPlatformOptions?.data.results.find(
    (platform) => platform.code === CONTACT_PLATFORM_CODE.WORK_EMAIL,
  );

  const contactPlatformWorkPhone = contactPlatformOptions?.data.results.find(
    (platform) => platform.code === CONTACT_PLATFORM_CODE.WORK_PHONE,
  );

  const contactPlatformLinkedin = contactPlatformOptions?.data.results.find(
    (platform) => platform.code === CONTACT_PLATFORM_CODE.LINKEDIN,
  );

  const [firstName, setFirstName] = useState<string>("");
  const onFirstNameChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setFirstName(event.target.value);

  const [lastName, setLastName] = useState<string>("");
  const onLastNameChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setLastName(event.target.value);

  const [email, setEmail] = useState<ContactFormValue>({
    id: undefined,
    value: "",
  });
  const onEmailChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setEmail(Object.assign({}, email, { value: event.target.value }));

  const [phone, setPhone] = useState<ContactFormValue>({
    id: undefined,
    value: "",
  });
  const onPhoneChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setPhone(Object.assign({}, phone, { value: event.target.value }));

  const [linkedin, setLinkedin] = useState<ContactFormValue>({
    id: undefined,
    value: "",
  });
  const onWebChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setLinkedin(Object.assign({}, linkedin, { value: event.target.value }));

  const [position, setPosition] = useState<string>("");
  const onPositionChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setPosition(event.target.value);

  const onUploadInstitutionLogo = async () => {
    if (!logoFile) return;

    const payload = { picture: logoFile };

    try {
      if (uuid) {
        const uploadFileResponse = await uploadInstitutionLogo.mutateAsync({
          institutionId: uuid,
          ...payload,
        });
        const presignedUrlResponse = await loadFile(uploadFileResponse.data.id);
        setUploadedInstitutionLogo({
          id: uploadFileResponse.data.id,
          url: presignedUrlResponse.data.presignedUrl,
        });
        appToastRef?.current?.show({
          severity: "success",
          summary: "Institution logo uploaded successfully.",
        });
      }
    } catch (error) {
      appToastRef?.current?.show({
        severity: "error",
        summary: "Failed to upload institution logo.",
      });
    }
  };

  const deleteInstitutionLogo = async (
    institutionId: string,
    setUploadedInstitutionLogo: (
      image: { url: string | null; id: string | null } | null,
    ) => void,
  ): Promise<void> => {
    try {
      await deleteLogo(institutionId);
      setUploadedInstitutionLogo(null);
      appToastRef?.current?.show({
        severity: "success",
        summary: "Institution logo deleted successfully.",
      });
    } catch (error) {
      appToastRef?.current?.show({
        severity: "error",
        summary: "Failed to delete institution logo.",
      });
    }
  };

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();

    let representativeId = representative;

    if (representativeType === representativeTypeValue.NEW) {
      const contacts: CreateRepresentativeContactPayload[] = [];

      if (email.value) {
        contacts.push({
          type_id: contactPlatformWorkEmail?.id || "",
          value: email.value,
        });
      }

      if (phone.value) {
        contacts.push({
          type_id: contactPlatformWorkPhone?.id || "",
          value: phone.value,
        });
      }

      if (linkedin.value) {
        contacts.push({
          type_id: contactPlatformLinkedin?.id || "",
          value: linkedin.value,
        });
      }

      const createRepresentativePayload: CreateRepresentativePayload = {
        first_name: firstName,
        last_name: lastName,
        contacts,
      };

      const { data } = await representativeCreate.mutateAsync(
        createRepresentativePayload,
      );
      representativeId = data.id;

      queryClient.invalidateQueries(queryKeys.representativeList(), {
        refetchInactive: true,
      });
    }

    if (uuid) {
      const payload: UpdateInstitutionPayload = {
        name,
        web,
        country,
        state: state || null,
        city,
        context,
        checklists,
      };

      if (representativeId && position) {
        payload.representative = {
          id: representativeId,
          position,
        };
      }

      await updateInstituon.mutateAsync(payload);

      queryClient.invalidateQueries(queryKeys.institution(uuid), {
        refetchInactive: true,
      });
    } else {
      const payload: CreateInstitutionPayload = {
        name,
        web,
        country,
        state: state || null,
        city,
        context,
        checklists,
      };

      if (representativeId && position) {
        payload.representative = {
          id: representativeId,
          position,
        };
      }

      await createInstituon.mutateAsync(payload);
    }

    queryClient.invalidateQueries(queryKeys.institutionList(), {
      refetchInactive: true,
    });

    navigate("/institutions");
  };

  const onDelete = async () => {
    await deleteInstituon.mutateAsync(uuid);

    queryClient.invalidateQueries(queryKeys.institution(uuid));
    queryClient.invalidateQueries(queryKeys.institutionList(), {
      refetchInactive: true,
    });

    navigate("/institutions");
  };

  const onCancel = () => navigate(-1);

  useEffect(() => {
    if (institutionResponse) {
      const { data: institution } = institutionResponse;

      const fetchInstitutionLogoIfNeeded = async () => {
        if (institutionResponse?.data.logo?.id) {
          await fetchProfilePicture(
            institutionResponse.data.logo.id,
            setUploadedInstitutionLogo,
          );
        } else {
          setUploadedInstitutionLogo(null);
        }
      };

      setName(institution.name);
      setWeb(institution.web);
      setCountry(institution.country ? institution.country.id : "");
      setState(institution.country_state ? institution.country_state.id : "");
      setCity(institution.city);
      setContext(institution.context.map((context) => context.id));
      setChecklists(institution.checklists.map((checklist) => checklist.id));
      fetchInstitutionLogoIfNeeded();
      if (institution.representatives_position.length > 0) {
        setRepresentative(
          institution.representatives_position[0].representative.id,
        );
        setPosition(institution.representatives_position[0].position);
      }
    }
  }, [
    institutionResponse,
    contactPlatformLinkedin,
    contactPlatformWorkEmail,
    contactPlatformWorkPhone,
  ]);

  return (
    <form onSubmit={handleSubmit} className="col-12">
      <Panel header="General" toggleable={true} className="mb-3">
        <div className="p-fluid">
          {uuid && (
            <div className="col-12 sm:col-3 md:col-5">
              <Panel
                header="Upload Logo Picture"
                toggleable={true}
                className="mb-3"
              >
                {uploadedInstitutionLogo?.url && (
                  <div>
                    <img
                      src={uploadedInstitutionLogo.url}
                      alt="Logo"
                      style={{ width: "200px", height: "auto" }}
                    />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                />
                <br />
                <div className="p-buttonset mt-2">
                  <Button
                    label="Upload Picture"
                    className=" p-button-outlined p-button-success"
                    onClick={(event) => {
                      event.preventDefault();
                      onUploadInstitutionLogo();
                    }}
                    disabled={!logoFile}
                  />
                  <Button
                    label="Delete Picture"
                    icon="pi pi-trash"
                    className=" p-button-outlined p-button-danger"
                    onClick={(event) => {
                      event.preventDefault();
                      deleteInstitutionLogo(uuid, () =>
                        setUploadedInstitutionLogo(null),
                      );
                    }}
                  />
                </div>
              </Panel>
            </div>
          )}
          {isAdmin() && uuid && !institutionResponse?.data.is_approved && (
            <Message
              severity="warn"
              text="This institution needs to be approved."
              className="mb-3"
            />
          )}

          <div className="field pt-3">
            <div className="p-float-label">
              <InputText value={name} onChange={onNameChange} />
              <label>Institution name *</label>
            </div>
          </div>

          <div className="grid formgrid pt-3">
            <div className="field col">
              <div className="p-float-label">
                <Dropdown
                  options={countryOptions?.data.results || []}
                  value={country}
                  onChange={onCountryChange}
                  optionLabel="name"
                  optionValue="id"
                />
                <label>Country *</label>
              </div>
            </div>

            <div className="field col">
              <div className="p-float-label">
                <InputText value={city} onChange={onCityChange} />
                <label>City *</label>
              </div>
            </div>

            <div className="field col">
              <div className="p-float-label">
                <Dropdown
                  options={countryStateOptions?.data.results || []}
                  value={state}
                  onChange={onStateChange}
                  optionLabel="name"
                  optionValue="id"
                  disabled={!country}
                />
                <label>State</label>
              </div>
            </div>
          </div>

          <div className="field pt-3">
            <div className="p-float-label">
              <InputText value={web} onChange={handleWebChange} />
              <label>Web *</label>
            </div>
            <small className={commonStyles.fieldHelp}>
              Web address starting with https://
            </small>
          </div>

          <div className="field pt-3">
            <div className="p-float-label">
              <MultiSelect
                options={journeyTypeOptions?.data.results || []}
                value={context}
                onChange={onContextChange}
                optionLabel="name"
                optionValue="id"
              />
              <label>Context *</label>
            </div>
          </div>

          {isAdmin() && (
            <div className="field pt-3">
              <div className="p-float-label">
                <MultiSelect
                  options={checklistOptions?.data.results || []}
                  value={checklists}
                  onChange={onChecklistChange}
                  optionLabel="name"
                  optionValue="id"
                />
                <label>Checklists *</label>
              </div>
            </div>
          )}
        </div>
      </Panel>

      <Panel header="Representative" toggleable={true} className="mb-3">
        <div className="grid">
          <div className="field col">
            <SelectButton
              options={representativeTypes}
              value={representativeType}
              onChange={onRepresentativeTypeChange}
              disabled={!isAdmin()}
              className="p-button-sm"
            />
          </div>
        </div>

        {representativeType === representativeTypeValue.EXISTING && (
          <div className="p-fluid">
            <div className="grid formgrid pt-3">
              <div className="field col">
                <div className="p-float-label">
                  <Dropdown
                    options={representativeOptions?.data.results || []}
                    value={representative}
                    onChange={onRepresentativeChange}
                    disabled={!isAdmin()}
                    optionValue="id"
                    optionLabel="name"
                    itemTemplate={tRepresentativeItem}
                    valueTemplate={tRepresentativeValue}
                  />
                  <label>Select Representative</label>
                </div>
              </div>

              <div className="field col">
                <div className="p-float-label">
                  <InputText
                    value={position}
                    onChange={onPositionChange}
                    disabled={!isAdmin()}
                  />
                  <label>Job Position</label>
                </div>
              </div>
            </div>
          </div>
        )}

        {representativeType === representativeTypeValue.NEW && (
          <div className="p-fluid">
            <div className="grid formgrid pt-3">
              <div className="field col">
                <div className="p-float-label">
                  <InputText value={firstName} onChange={onFirstNameChange} />
                  <label>First Name</label>
                </div>
              </div>

              <div className="field col">
                <div className="p-float-label">
                  <InputText value={lastName} onChange={onLastNameChange} />
                  <label>Last Name</label>
                </div>
              </div>
            </div>

            <div className="grid formgrid pt-3">
              <div className="field col">
                <div className="p-float-label">
                  <InputText value={position} onChange={onPositionChange} />
                  <label>Job Position</label>
                </div>
              </div>

              <div className="field col">
                <div className="p-float-label">
                  <InputText value={email.value} onChange={onEmailChange} />
                  <label>E-mail Address</label>
                </div>
              </div>
            </div>

            <div className="grid formgrid pt-3">
              <div className="field col">
                <div className="p-float-label">
                  <InputText value={phone.value} onChange={onPhoneChange} />
                  <label>Phone number</label>
                </div>
              </div>

              <div className="field col">
                <div className="p-float-label">
                  <InputText value={linkedin.value} onChange={onWebChange} />
                  <label>LinkedIn</label>
                </div>
              </div>
            </div>
          </div>
        )}
      </Panel>

      <div className="flex gap-1 justify-content-end">
        <Button
          type="button"
          label="Cancel"
          className="p-button-sm p-button-secondary"
          text={true}
          onClick={onCancel}
        />

        {uuid && (
          <Button
            type="button"
            label="Delete"
            className="p-button-sm p-button-danger"
            outlined={true}
            onClick={onDelete}
          />
        )}

        <Button
          type="submit"
          label="Save"
          className="p-button-main p-button-sm"
          disabled={!name || !country || !city || !web || !context}
        />
      </div>
    </form>
  );
}

interface ContactFormValue {
  value: string;
  id: string | undefined;
}

const representativeTypeValue = {
  NEW: "new",
  EXISTING: "existing",
};

interface RepresentativeType {
  label: string;
  value: string;
}
